"use client";

import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, Sparkles, Filter } from 'lucide-react';
import PropertyCard, { PropertyCardProps } from './PropertyCard';
import SponsoredAdSlot from './SponsoredAdSlot';

interface SponsoredPropertyGridProps {
  title: string;
  subtitle?: string;
  properties: PropertyCardProps['property'][];
  className?: string;
}

const SponsoredPropertyGrid = ({
  title,
  subtitle,
  properties,
  className = ''
}: SponsoredPropertyGridProps) => {
  const [visibleCount, setVisibleCount] = useState(9);
  const [filteredProperties, setFilteredProperties] = useState(properties);
  const [activeSort, setActiveSort] = useState('featured');
  const loadMoreRef = useRef(null);
  const isInView = useInView(loadMoreRef, { once: false, amount: 0.1 });
  
  // Reset visible count when properties change
  useEffect(() => {
    setFilteredProperties(properties);
  }, [properties]);
  
  // Trigger animations when scrolled into view
  useEffect(() => {
    if (isInView && visibleCount < filteredProperties.length) {
      const timer = setTimeout(() => {
        setVisibleCount(prevCount => Math.min(prevCount + 6, filteredProperties.length));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isInView, filteredProperties.length, visibleCount]);
  
  // Sort properties
  const handleSort = (sortType: string) => {
    setActiveSort(sortType);
    const sorted = [...properties];
    
    switch(sortType) {
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        sorted.sort((a, b) => (b.daysListed || 999) - (a.daysListed || 999));
        break;
      case 'rating':
        sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
        break;
      default: // featured
        // Keep original order
        break;
    }
    
    setFilteredProperties(sorted);
    setVisibleCount(9); // Reset to show first page
  };
  
  // Prepare the data with ads inserted
  const prepareGridItems = () => {
    const items = [];
    const visibleProperties = filteredProperties.slice(0, visibleCount);
    
    // Ad insertion positions (after which property to insert ads)
    const adPositions = [2, 5, 8, 11, 14, 17, 20];
    
    // Ad types to cycle through
    const adTypes: Array<'google' | 'native' | 'sponsor' | 'promotion'> = [
      'google', 'sponsor', 'native', 'promotion', 'google', 'sponsor', 'native'
    ];
    
    let adCounter = 0;
    
    // Add properties and ads
    visibleProperties.forEach((property, index) => {
      // Add the property
      items.push({
        type: 'property',
        data: property,
        key: property.id
      });
      
      // Check if we should add an ad after this property
      if (adPositions.includes(index)) {
        items.push({
          type: 'ad',
          data: {
            type: adTypes[adCounter % adTypes.length],
            position: adCounter + 1
          },
          key: `ad-${adCounter}`
        });
        adCounter++;
      }
    });
    
    return items;
  };
  
  const gridItems = prepareGridItems();

  return (
    <section className={`${className}`}>
      {/* Header with title and subtitle */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="p-1.5 rounded-lg bg-[var(--color-sponsored-primary)]/20 text-[var(--color-sponsored-primary)]">
            <Sparkles className="h-5 w-5" />
          </span>
          <h2 className="text-xl md:text-2xl font-bold text-[var(--color-sponsored-primary)]">{title}</h2>
          <div className="h-px flex-1 bg-gradient-to-r from-[var(--color-sponsored-primary)]/30 to-transparent"></div>
        </div>
        
        {subtitle && (
          <p className="text-[var(--color-neutral-600)] text-sm md:text-base max-w-3xl">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Sorting options */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-[var(--color-neutral-50)] p-3 rounded-lg">
        <div className="flex items-center">
          <Badge variant="outline" className="bg-[var(--color-pure-white)] text-[var(--color-sponsored-primary)] border-[var(--color-sponsored-primary)]/30 flex items-center gap-1 px-3 py-1.5">
            <Filter className="h-3.5 w-3.5" />
            <span>Sort by:</span>
          </Badge>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {[
            { id: 'featured', label: 'Featured' },
            { id: 'price-asc', label: 'Price: Low to High' },
            { id: 'price-desc', label: 'Price: High to Low' },
            { id: 'newest', label: 'Newest' },
            { id: 'rating', label: 'Top Rated' },
          ].map(sort => (
            <Button 
              key={sort.id}
              variant={activeSort === sort.id ? "default" : "outline"} 
              size="sm"
              onClick={() => handleSort(sort.id)}
              className={activeSort === sort.id 
                ? "bg-[var(--color-sponsored-primary)] hover:bg-[var(--color-sponsored-primary)]/90 text-[var(--color-pure-white)] border-0"
                : "bg-[var(--color-pure-white)] text-[var(--color-neutral-700)] hover:text-[var(--color-sponsored-primary)] hover:border-[var(--color-sponsored-primary)]/30 border"
              }
            >
              {sort.label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Property Grid with ads */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {gridItems.map((item, index) => (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.5,
              delay: Math.min(0.3, index * 0.05),
              ease: "easeOut" as const
            }}
          >
            {item.type === 'property' ? (
              <PropertyCard 
                property={item.data as PropertyCardProps['property']}
                animationDelay={0} // We're handling the animation in the parent div
              />
            ) : (
              <SponsoredAdSlot 
                type={item.data.type}
                position={item.data.position}
                animationDelay={0.1} // Slight delay for ads
              />
            )}
          </motion.div>
        ))}
      </motion.div>
      
      {/* Load more section */}
      {visibleCount < filteredProperties.length && (
        <div className="mt-12 text-center" ref={loadMoreRef}>
          <Button
            onClick={() => setVisibleCount(prev => Math.min(prev + 6, filteredProperties.length))}
                          className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white px-8 py-2 shadow-md"
            size="lg"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            Load More Properties
          </Button>
          <p className="mt-2 text-sm text-[var(--color-neutral-500)]">
            Showing {visibleCount} of {filteredProperties.length} properties
          </p>
        </div>
      )}
    </section>
  );
};

export default SponsoredPropertyGrid; 